'use client';

import type { ButtonHTMLAttributes, FC, HTMLAttributes, ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { useUiMotionEnabled } from '../../hooks/useMotion';
import { Spinner } from './Spinner';
import './Button.css';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'destructiveSecondary'
    | 'success'
    | 'successSecondary'
    | 'black'
    | 'white'
    | 'share'
    | 'shareBigWin'
    | 'cta'
    | 'ctaPurple';
  mode?: 'primary' | 'secondary' | 'tertiary' | 'black' | 'success';
  size?: 's' | 'm' | 'l';
  stretched?: boolean;
  loading?: boolean;
  before?: ReactNode;
  after?: ReactNode;
  iconPosition?: 'left' | 'top';
  /** Render as a non-interactive `<span>` with the same look, for a CTA-styled
   *  pill placed inside an already-clickable parent (e.g. a card that is
   *  itself a `<button>`) — a real nested `<button>` there is invalid HTML. */
  as?: 'button' | 'span';
}

// Mirrors Apple iOS 26 "Button - Content Area" (Figma Buttons page):
//   primary              → Style=Bordered-Prominent          (accent-blue fill, white text)
//   secondary            → Style=Bordered                    (fill-tertiary, accent-blue text)
//   tertiary             → Style=Bordered - Secondary        (fill-tertiary, label-primary text)
//   destructiveSecondary → Style=Bordered,           Dest=Y  (fill-tertiary, accent-red  text)
// Sizes match Figma:
//   Small  — px-[10px] py-[4px], 15/20, tracking -0.23
//   Medium — px-[14px] py-[7px], 15/20, tracking -0.23
//   Large  — px-[20px] py-[14px], 17/22, tracking -0.43
// Borderless / "ghost" intentionally omitted.
// share/shareBigWin/cta/ctaPurple keep custom gradients (not in Apple HIG).
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 border-none rounded-full font-normal cursor-pointer transition-colors duration-200 ease-smooth relative overflow-hidden outline-none whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 active:shadow-[inset_0_0_0_9999px_rgba(255,255,255,0.14)]',
  {
    variants: {
      variant: {
        primary: 'bg-accent-blue text-white',
        secondary: 'bg-bg-secondary text-accent-blue',
        tertiary: 'bg-bg-secondary text-label-primary',
        destructiveSecondary: 'bg-bg-secondary text-accent-red',
        black: 'bg-black text-white',
        // Light fills take the tint with the opposite sign: white over white is a step of
        // 1.000:1, i.e. no feedback at all.
        white: 'bg-white text-black font-semibold active:shadow-[inset_0_0_0_9999px_rgba(0,0,0,0.10)]',
        success: 'bg-accent-green text-white',
        successSecondary: 'bg-bg-secondary text-accent-green',
        share: 'bg-gradient-to-r from-[#6C5CE7] to-[#a855f7] text-white',
        // The gold glow has to be restated inside the pressed rule: box-shadow replaces the
        // whole list, so tinting without it would drop the glow for as long as the finger is down.
        shareBigWin:
          'bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black shadow-[0_0_20px_rgba(255,215,0,0.25)] active:shadow-[inset_0_0_0_9999px_rgba(0,0,0,0.10),0_0_20px_rgba(255,215,0,0.25)]',
        cta: 'bg-accent-blue text-white relative overflow-hidden before:absolute before:inset-0 before:bg-[linear-gradient(110deg,transparent_25%,rgba(255,255,255,0.3)_50%,transparent_75%)] before:bg-[length:200%_100%] before:animate-[ctaShimmer_4s_ease-in-out_infinite]',
        ctaPurple: 'bg-[linear-gradient(135deg,#a855f7_0%,#ec4899_100%)] text-white relative overflow-hidden before:absolute before:inset-0 before:bg-[linear-gradient(110deg,transparent_25%,rgba(255,255,255,0.3)_50%,transparent_75%)] before:bg-[length:200%_100%] before:animate-[ctaShimmer_4s_ease-in-out_infinite]',
      },
      size: {
        s: 'px-[10px] py-[4px] text-[15px] leading-[20px] tracking-[-0.23px]',
        m: 'px-[14px] py-[7px] text-[15px] leading-[20px] tracking-[-0.23px]',
        l: 'px-[20px] py-[14px] text-[17px] leading-[22px] tracking-[-0.43px]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'm',
    },
  },
);

export const Button: FC<ButtonProps> = ({
  children,
  variant = 'primary',
  mode,
  size = 'm',
  stretched = false,
  loading = false,
  disabled,
  before,
  after,
  iconPosition = 'left',
  className,
  as = 'button',
  ...props
}) => {
  const variantClass = mode ? mode : variant;
  const isIconTop = iconPosition === 'top';
  const motionEnabled = useUiMotionEnabled();

  const content = (
    <>
      {/* Подпись остаётся в потоке, только невидимой, а индикатор рисуется поверх по центру:
       *  подмена содержимого спиннером схлопывала кнопку до кружка. `contents` не создаёт
       *  собственного бокса, поэтому before/children/after остаются прямыми флекс-элементами
       *  кнопки и раскладка в покое не меняется. */}
      <span className={cn('contents', loading && 'invisible')}>
        {before && <span className={cn('flex items-center', !isIconTop && 'me-2')}>{before}</span>}
        {children}
        {after && <span className={cn('flex items-center', !isIconTop && 'ms-2')}>{after}</span>}
      </span>
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner size="s" color="white" />
        </span>
      )}
    </>
  );

  const sharedClassName = cn(
    buttonVariants({ variant: variantClass, size }),
    motionEnabled && 'shot-press-tint',
    stretched && 'w-full',
    isIconTop && 'flex-col gap-1',
    className,
  );

  if (as === 'span') {
    return (
      <span className={sharedClassName} {...(props as HTMLAttributes<HTMLSpanElement>)}>
        {content}
      </span>
    );
  }

  return (
    <button className={sharedClassName} disabled={disabled || loading} aria-busy={loading || undefined} {...props}>
      {content}
    </button>
  );
};
